import { useAuth0 } from '@auth0/auth0-react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from '@tempo-io/sui-navigation';
import { Toaster } from '@tempo-io/sui-sonner';
import { NotFoundError } from '@/components/common/error/NotFoundError';
import {
  SidebarExpansionProvider,
  useSidebarExpansionContext,
} from '@/components/common/sidebar/SidebarExpansionContext';
import { ThemeProvider, useTheme } from '@/components/common/theme-provider';
import type { RouterContext } from '@/router';

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => {
    return (
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <SidebarExpansionProvider>
          <RootComponent />
        </SidebarExpansionProvider>
      </ThemeProvider>
    );
  },
  notFoundComponent: NotFoundError,
});

function RootComponent() {
  const { isLoading } = useAuth0();
  const { isExpanded } = useSidebarExpansionContext();
  const { theme } = useTheme();

  if (isLoading) {
    return null;
  }

  const curSidebarWidth = isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;
  const marginLeft = 16;
  const toastOffset = curSidebarWidth + marginLeft;

  return (
    <>
      <div data-testid="e2e-authenticated" className="flex h-screen">
        {!isLoading && <Outlet />}
      </div>
      <Toaster theme={theme} closeButton position="bottom-left" offset={{ left: toastOffset }} />
      <ReactQueryDevtools initialIsOpen={false} />
      <TanStackRouterDevtools position="bottom-right" />
    </>
  );
}
